// src/contexts/AuthContext.tsx - SÖZLEŞME SİSTEMİ EKLENMİŞ FULL VERSİYON
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '../services/api';
import { applyPrivacyMode } from '../utils/format';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  organizationName?: string;
  organizationCode?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, fullName: string, password: string, phoneNumber: string, organizationCode?: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  // YENİ: Sözleşme
  hasAcceptedTerms: boolean;
  acceptTerms: () => Promise<void>;
  declineTerms: () => Promise<void>;
  checkTermsStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const isAuthenticated = !!user;

  // Sözleşme durumunu API'den kontrol et
  const checkTermsStatus = useCallback(async () => {
    try {
      if (!apiService.isAuthenticated()) return;
      const result = await apiService.getTermsStatus();
      setHasAcceptedTerms(result.hasAcceptedTerms);
      localStorage.setItem('hasAcceptedTerms', String(result.hasAcceptedTerms));
    } catch (error) {
      console.error('Terms status check failed:', error);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // GİZLİLİK MODU: oturum açıkken sunucudaki modu çek ve uygula.
  // localStorage'daki değer ilk render'da zaten maskeyi hazır tutuyor; buradaki
  // taze değer onu ezer (başka cihazdan değiştirilmişse de senkron olur).
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    apiService.getPluginSettings()
      .then(ps => { if (!cancelled) applyPrivacyMode(ps?.privacyMode); })
      .catch(() => { /* ayar çekilemezse mevcut (localStorage) değer geçerli kalır */ });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // YENİ: Tab geri geldiğinde sözleşme durumunu kontrol et
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        checkTermsStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, checkTermsStatus]);

  // YENİ: Periyodik kontrol (her 60 saniyede) - deploy anında yakalamak için
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      checkTermsStatus();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [isAuthenticated, checkTermsStatus]);

  // "Son görülme" heartbeat — dashboard açıkken periyodik + sekme öne gelince
  useEffect(() => {
    if (!isAuthenticated) return;
    apiService.sendHeartbeat(); // hemen
    const onVisible = () => { if (document.visibilityState === 'visible') apiService.sendHeartbeat(); };
    document.addEventListener('visibilitychange', onVisible);
    const hb = setInterval(() => { if (document.visibilityState === 'visible') apiService.sendHeartbeat(); }, 180000); // 3 dk
    return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(hb); };
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      if (apiService.isAuthenticated()) {
        const storedData = apiService.getStoredUserData();
        if (storedData.email && storedData.fullName) {
          setUser({
            id: storedData.userId || '',
            email: storedData.email,
            fullName: storedData.fullName,
            role: storedData.role || 'User',
            organizationName: storedData.organizationName || undefined,
            organizationCode: storedData.organizationCode || undefined,
          });

          // Stored terms durumunu oku, sonra API'den doğrula
          const storedTerms = storedData.hasAcceptedTerms;
          setHasAcceptedTerms(storedTerms);

          // Arka planda API'den güncel durumu al
          // Bu sayede deploy anında localStorage'da true olsa bile
          // API false dönerse modal açılır
          try {
            const result = await apiService.getTermsStatus();
            setHasAcceptedTerms(result.hasAcceptedTerms);
            localStorage.setItem('hasAcceptedTerms', String(result.hasAcceptedTerms));
          } catch {
            // API hatası olursa stored değerle devam et
          }
        } else {
          const userData = await apiService.getCurrentUser();
          setUser({
            id: userData.id,
            email: userData.email,
            fullName: userData.fullName,
            role: userData.role,
          });
          // getCurrentUser'dan sonra terms status'u da al
          await checkTermsStatus();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      apiService.clearAuthData();
      setUser(null);
      setHasAcceptedTerms(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await apiService.login({ email, password });

      apiService.saveAuthData(response);

      setUser({
        id: response.userId,
        email: response.email,
        fullName: response.fullName,
        role: response.role,
        organizationName: response.organizationName,
        organizationCode: response.organizationCode,
      });

      // YENİ: Sözleşme durumunu set et
      setHasAcceptedTerms(response.hasAcceptedTerms);

      if (response.organizationName) {
        toast.success('Giriş başarılı!');
      } else {
        toast.success('Giriş başarılı!');
      }
    } catch (error: any) {
      const message = error?.message || 'Giriş başarısız oldu';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    fullName: string,
    password: string,
    phoneNumber: string,
    organizationCode?: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const signupSource = localStorage.getItem('signupSource') || undefined;
      const response = await apiService.register({
        email,
        fullName,
        password,
        phoneNumber,
        organizationCode,
        signupSource,
      });
      localStorage.removeItem('signupSource'); // tüketildi

      apiService.saveAuthData(response);

      setUser({
        id: response.userId,
        email: response.email,
        fullName: response.fullName,
        role: response.role,
        organizationName: response.organizationName,
        organizationCode: response.organizationCode,
      });

      // YENİ: Yeni kayıtta sözleşme onaylanmamış
      setHasAcceptedTerms(false);

      toast.success('Kayıt başarılı!');
    } catch (error: any) {
      const message = error?.message || 'Kayıt başarısız oldu';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // YENİ: Sözleşmeyi onayla
  const acceptTerms = async (): Promise<void> => {
    try {
      const result = await apiService.acceptTerms();
      setHasAcceptedTerms(true);
      localStorage.setItem('hasAcceptedTerms', 'true');
      toast.success(result.message || 'Sözleşme onaylandı!');
    } catch (error: any) {
      toast.error(error?.message || 'Sözleşme onaylanırken hata oluştu');
      throw error;
    }
  };

  // YENİ: Sözleşmeyi reddet
  const declineTerms = async (): Promise<void> => {
    try {
      await apiService.declineTerms();
      setHasAcceptedTerms(false);
      localStorage.setItem('hasAcceptedTerms', 'false');
      // Hesap pasif oldu, çıkış yap
      logout();
      toast.error('Sözleşme reddedildi. Hesabınız pasif duruma alındı.');
    } catch (error: any) {
      toast.error(error?.message || 'İşlem sırasında hata oluştu');
      throw error;
    }
  };

  const logout = () => {
    apiService.clearAuthData();
    setUser(null);
    setHasAcceptedTerms(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        register,
        logout,
        isAuthenticated,
        hasAcceptedTerms,
        acceptTerms,
        declineTerms,
        checkTermsStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}